import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useOrganization } from '@/contexts/organization-context';
import { useApi } from '@/hooks/useApi';
import { approvalClient } from '@/lib/approval-client';
import type {
  ApprovalBox,
  ApprovalListFilter,
  ApprovalProgram,
  ApprovalRequestSummary,
  ApprovalView,
  MyProfile,
} from '@/types/approval';
import type { Organization } from '@/lib/api/data-contracts';
import { DealerPriceDialog } from './dealer-price-dialog';
import { StorePriceDialog } from './store-price-dialog';
import { ApprovalDetailScreen } from './approval-detail';
import {
  contractPriceLabel,
  formatDateTime,
  formatNumber,
  PROGRAM_TITLE,
  requestDataValue,
  StatusBadge,
  useMyProfile,
  VIEW_OPTIONS,
} from './approval-common';
import { ApprovalSearch, EMPTY_DRAFT, screenSpec, storesUnderOrg } from './approval-search';
import type { SearchDraft, SearchField } from './approval-search';
import { FileSearch, FileText, Plus, RefreshCw } from 'lucide-react';

const PROGRAMS: ApprovalProgram[] = ['store-price', 'dealer-price'];

/** PRD 는 페이지네이션이 아니라 헤더 고정 스크롤이다. 조회기간 기본값이 1개월이라 한 번에 받아도 된다 (api 상한 200) */
const PAGE_SIZE = 200;

interface Column {
  header: string;
  /** 오른쪽 정렬(금액)·가운데 정렬(코드/일자) */
  align?: 'center' | 'right';
  width?: string;
  render: (row: ApprovalRequestSummary) => ReactNode;
}

const rd = (row: ApprovalRequestSummary, key: string) => requestDataValue(row.requestData, key);

/** 사유 칸은 취소 건이면 취소요청사유로 바뀐다 (PRD 공통) */
function reasonOf(row: ApprovalRequestSummary): string {
  return row.cancelReason ?? rd(row, 'requestReason');
}

/** 적용구분이 현재 계약값과 다르면 "(전환)" 을 붙인다 (B220 §3.2) */
function applyTypeWithSwitch(row: ApprovalRequestSummary): ReactNode {
  const current = rd(row, 'currentApplyTypeName');
  const next = rd(row, 'applyTypeName');
  const switched = current !== '-' && current !== next;
  return (
    <span title={switched ? `${current} → ${next}` : undefined}>
      {next}
      {switched && <span className='ml-1 text-[11px] text-amber-600'>(전환)</span>}
    </span>
  );
}

function delta(row: ApprovalRequestSummary): ReactNode {
  const value = Number(row.requestData?.differenceAmount ?? NaN);
  if (Number.isNaN(value)) return '-';
  const tone = value > 0 ? 'text-red-600' : value < 0 ? 'text-blue-600' : 'text-muted-foreground';
  return <span className={tone}>{`${value > 0 ? '+' : ''}${formatNumber(value)}`}</span>;
}

/**
 * PRD §3 목록 그리드 컬럼. 기안함(B210 13개 / B220 12개)은 요청번호·요청자·처리상태·요청일시가 더 있고,
 * 결재함(A330 11개 / A340 10개)은 요청부서명으로 시작한다.
 */
function columnsOf(program: ApprovalProgram, box: ApprovalBox): Column[] {
  const requestBox = box === 'requests';
  const head: Column[] = [
    ...(requestBox
      ? [{ header: '요청번호', align: 'center' as const, width: 'w-44', render: (r: ApprovalRequestSummary) => <span className='font-mono text-xs'>{r.documentNo}</span> }]
      : []),
    { header: requestBox ? '사업장' : '요청부서명', render: r => r.orgName ?? r.orgCd ?? '-' },
    ...(requestBox ? [{ header: '요청자', align: 'center' as const, render: (r: ApprovalRequestSummary) => r.drafterName ?? '-' }] : []),
  ];

  const item: Column[] =
    program === 'store-price'
      ? [
          { header: '상품명', render: r => rd(r, 'productName') },
          { header: '적용구분', align: 'center', render: r => rd(r, 'applyTypeName') },
          { header: '단가적용일', align: 'center', render: r => rd(r, 'applyDate') },
          { header: '적용차수', align: 'center', render: r => rd(r, 'applyRound') },
          { header: '현재단가', align: 'right', render: r => formatNumber(r.requestData?.currentPrice) },
          { header: '요청단가', align: 'right', render: r => <span className='font-semibold'>{formatNumber(r.requestData?.requestedPrice)}</span> },
          { header: '변동액', align: 'right', render: delta },
        ]
      : [
          { header: '거래처명', render: r => rd(r, 'dealerName') },
          { header: '유종/판매구분', align: 'center', render: r => `${rd(r, 'productName')} / ${rd(r, 'oilSaleTypeName')}` },
          { header: '적용구분', align: 'center', render: applyTypeWithSwitch },
          { header: '단가적용일', align: 'center', render: r => rd(r, 'applyDate') },
          // 단가적용방법마다 단위가 달라(% / 원) 방법과 함께 보여주고 변동액 열은 두지 않는다
          { header: '현재단가', align: 'right', render: r => contractPriceLabel(r.requestData?.currentPrice, r.requestData?.currentPriceAppMethod, r.requestData?.currentPriceAppMethodName) },
          {
            header: '요청단가',
            align: 'right',
            render: r => (
              <span className='font-semibold'>
                {contractPriceLabel(r.requestData?.requestedPrice, r.requestData?.priceAppMethod, r.requestData?.priceAppMethodName)}
              </span>
            ),
          },
        ];

  const tail: Column[] = [
    ...(requestBox ? [] : [{ header: '승인요청자', align: 'center' as const, render: (r: ApprovalRequestSummary) => r.drafterName ?? '-' }]),
    {
      header: '승인요청사유',
      width: 'min-w-48 max-w-64',
      render: r => (
        <span className='block truncate' title={reasonOf(r)}>
          {reasonOf(r)}
        </span>
      ),
    },
    { header: requestBox ? '처리상태' : '승인여부', align: 'center', render: r => <StatusBadge status={r.status} /> },
    ...(requestBox
      ? [{ header: '요청일시', align: 'center' as const, width: 'w-36', render: (r: ApprovalRequestSummary) => formatDateTime(r.createdAt) }]
      : []),
  ];

  return [...head, ...item, ...tail];
}

/**
 * 화면에 있는 칸만 조건으로 보낸다 - 없는 칸의 URL 값이 남아 있어도 조회에 끼지 않는다.
 * 직책 조회범위가 SELF·DEPT 면 URL 값과 무관하게 내 소속으로 고정한다 (입력칸도 잠겨 있다)
 */
function buildFilter(
  fields: SearchField[],
  draft: SearchDraft,
  profile: MyProfile | null,
  storesUnder: (deptCd: string) => string[]
): ApprovalListFilter {
  const filter: ApprovalListFilter = {};
  const hasOrg = fields.includes('org') || fields.includes('reqOrg');
  const orgCd = profile?.searchScope === 'SELF' ? profile.orgCd : draft.orgCd;
  const deptCd = profile?.searchScope === 'DEPT' ? profile.orgCd : draft.deptCd;
  const target = profile?.searchScope === 'SELF' || (hasOrg && orgCd) ? orgCd : deptCd;
  if (target) {
    // 고른 코드가 매장이 아니라 회사·부서일 수 있다 (web.user.org_cd 는 회사 코드인 경우가 있다).
    // approval.request.org_cd 는 매장(리프)이라 하위 매장으로 편다. 하위가 없으면 코드 자체를 보낸다
    const stores = storesUnder(target);
    filter.orgCds = stores.length > 0 ? stores : [target];
  }
  if (fields.includes('period')) {
    if (draft.from) filter.from = draft.from;
    if (draft.to) filter.to = draft.to;
  }
  if (fields.includes('product') && draft.productCode) filter.productCode = draft.productCode;
  if (fields.includes('applyType') && draft.applyType) filter.applyType = draft.applyType;
  if (fields.includes('dealer') && draft.dealerCode) filter.dealerCode = draft.dealerCode;
  return filter;
}

/** 하단 상태 바의 "현재 조회 사업장 범위" (B220 §3.2) */
function scopeLabel(draft: SearchDraft, profile: MyProfile | null, organizations: Organization[]): string {
  const name = (code: string) => organizations.find(o => o.orgCd === code)?.orgName ?? code;
  if (profile?.searchScope === 'SELF') return name(profile.orgCd);
  if (profile?.searchScope === 'DEPT') return `${name(profile.orgCd)} 산하`;
  if (draft.orgCd) return name(draft.orgCd);
  if (draft.deptCd) return `${name(draft.deptCd)} 산하`;
  return '전체';
}

const ALIGN = { center: 'text-center', right: 'text-right tabular-nums' } as const;

/**
 * 기안함(B210/B220) · 결재함(A330/A340). PRD §3 구성 그대로 조회조건 → 목록 그리드 → 하단 2분할 상세다.
 * 선택·조회조건·조회구분은 전부 URL 에 둔다 (프로그램을 옮기면 경로가 바뀌어 저절로 초기화된다).
 */
export function ApprovalListScreen({ box }: { box: ApprovalBox }) {
  const { program: programParam } = useParams();
  const program = (PROGRAMS.includes(programParam as ApprovalProgram) ? programParam : 'store-price') as ApprovalProgram;

  const [params, setParams] = useSearchParams();
  const patch = useCallback(
    (changes: Record<string, string | null>) =>
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) v === null ? next.delete(k) : next.set(k, v);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const { fields, defaultView } = screenSpec(box, program);
  const { organizations } = useOrganization();
  const profile = useMyProfile();

  const selectedId = Number(params.get('id')) || null;
  const view = (VIEW_OPTIONS.find(v => v.value === params.get('view'))?.value ?? defaultView) as ApprovalView;
  const dialogOpen = params.has('new');
  const NewDialog = program === 'store-price' ? StorePriceDialog : DealerPriceDialog;

  // 조회기간 기본값은 오늘로부터 1개월 전 ~ 오늘 (B210 §4.1). 조회기간 칸이 없는 화면(A340)은 기간을 안 건다
  const period = useMemo(
    () =>
      fields.includes('period')
        ? { from: dayjs().subtract(1, 'month').format('YYYY-MM-DD'), to: dayjs().format('YYYY-MM-DD') }
        : { from: '', to: '' },
    [fields]
  );
  const applied: SearchDraft = useMemo(
    () => ({
      ...EMPTY_DRAFT,
      orgCd: params.get('orgCd') ?? '',
      deptCd: params.get('deptCd') ?? '',
      from: params.get('from') ?? period.from,
      to: params.get('to') ?? period.to,
      productCode: params.get('productCode') ?? '',
      applyType: params.get('applyType') ?? '',
      dealerCode: params.get('dealerCode') ?? '',
    }),
    [params, period]
  );
  const filter = useMemo(
    () => buildFilter(fields, applied, profile, code => storesUnderOrg(organizations, code)),
    // organizations 가 늦게 도착하면 부서 전개 결과가 바뀐다
    [fields, applied, profile, organizations]
  );
  // useApi 의 dep 은 값 비교라 객체를 그대로 넣으면 매 렌더 재조회한다
  const filterKey = JSON.stringify(filter);

  const select = useCallback((id: number | null) => patch({ id: id ? String(id) : null }), [patch]);

  const { data, loading, error, refetch } = useApi(
    () => approvalClient.list(box, program, { view, filter, size: PAGE_SIZE }),
    [box, program, view, filterKey]
  );
  // 집계는 조회구분과 무관하다 - 조회조건이 바뀔 때만 다시 센다
  const { data: counts, refetch: refetchCounts } = useApi(
    () => approvalClient.counts(box, program, filter),
    [box, program, filterKey]
  );

  const rows = data?.content ?? [];
  // 총계는 조회구분과 무관한 전체 건수, 절단 경고는 지금 고른 조회구분의 건수와 비교해야 한다
  const total = counts?.total ?? data?.page?.totalElements ?? rows.length;
  const viewTotal = data?.page?.totalElements ?? rows.length;
  const columns = useMemo(() => columnsOf(program, box), [program, box]);

  const reload = useCallback(() => {
    select(null);
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts, select]);

  const handleSubmitted = useCallback(
    (requestId: number) => {
      refetch();
      refetchCounts();
      // 다이얼로그 닫기(new 제거)와 선택(id)을 한 번에 바꾼다 - setSearchParams 를 연달아 부르면 뒤 호출이
      // 앞 호출 전 URL 을 기준으로 덮어써 new=1 이 살아남고 상신한 다이얼로그가 다시 열린다
      patch({ new: null, id: String(requestId) });
    },
    [refetch, refetchCounts, patch]
  );

  return (
    <div className='flex min-h-0 flex-col gap-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <h1 className='min-w-0 text-base font-semibold leading-tight'>
          {PROGRAM_TITLE[program]} {box === 'requests' ? '승인요청 등록' : '승인등록'}
        </h1>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={reload} title='새로고침'>
            <RefreshCw className='size-4' />
          </Button>
          {box === 'requests' && (
            // 데스크톱은 레일 상단 버튼이 맡는다 (레일은 md 이상에서만 보인다)
            <Button size='sm' className='md:hidden' onClick={() => patch({ new: '1' })}>
              <Plus className='mr-1 size-4' />
              신규요청
            </Button>
          )}
        </div>
      </div>

      <ApprovalSearch box={box} program={program} applied={applied} view={view} profile={profile} onApply={patch} />

      <section className='rounded-lg border bg-card'>
        <div className='max-h-[26rem] overflow-auto'>
          <table className='w-full caption-bottom text-sm'>
            <TableHeader className='sticky top-0 z-10 bg-card'>
              <TableRow>
                {columns.map(col => (
                  <TableHead key={col.header} className={`${col.align ? ALIGN[col.align] : ''} ${col.width ?? ''} whitespace-nowrap`}>
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={columns.length}>
                      <Skeleton className='h-5 w-full' />
                    </TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className='py-10 text-center text-muted-foreground'>
                    <p className='mb-3 text-sm'>목록을 불러오지 못했습니다.</p>
                    <Button variant='outline' size='sm' onClick={() => refetch()}>
                      다시 시도
                    </Button>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className='py-10 text-center text-muted-foreground'>
                    <FileSearch className='mx-auto mb-2 size-8 opacity-60' />
                    <span className='text-sm'>조회 결과가 없습니다</span>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow
                    key={row.id}
                    onClick={() => select(row.id)}
                    aria-selected={row.id === selectedId}
                    className={`cursor-pointer ${row.id === selectedId ? 'bg-accent' : ''}`}
                  >
                    {columns.map(col => (
                      <TableCell key={col.header} className={`${col.align ? ALIGN[col.align] : ''} ${col.width ?? ''} py-2`}>
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </table>
        </div>
        <CountsFooter
          total={total}
          counts={counts}
          viewTotal={viewTotal}
          loaded={rows.length}
          scope={scopeLabel(applied, profile, organizations)}
        />
      </section>

      {selectedId ? (
        <ApprovalDetailScreen id={selectedId} box={box} program={program} onDone={reload} />
      ) : (
        <section className='flex min-h-[10rem] items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground'>
          <div className='text-center'>
            <FileText className='mx-auto mb-2 size-8 opacity-50' />
            목록에서 항목을 선택하세요
          </div>
        </section>
      )}

      {box === 'requests' && (
        <NewDialog open={dialogOpen} onOpenChange={open => patch({ new: open ? '1' : null })} onSubmitted={handleSubmitted} />
      )}
    </div>
  );
}

/** PRD 하단 집계 6종. 반려는 조회구분에 없고 여기에만 나온다 */
function CountsFooter({
  total,
  counts,
  viewTotal,
  loaded,
  scope,
}: {
  total: number;
  counts?: { unapproved: number; approved: number; rejected: number; cancelUnapproved: number; cancelApproved: number } | null;
  /** 지금 고른 조회구분의 전체 건수 */
  viewTotal: number;
  loaded: number;
  scope: string;
}) {
  const items = [
    ['미승인', counts?.unapproved],
    ['승인', counts?.approved],
    ['반려', counts?.rejected],
    ['취소미승인', counts?.cancelUnapproved],
    ['취소승인', counts?.cancelApproved],
  ] as const;
  return (
    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-3 py-2 text-xs text-muted-foreground'>
      <span>
        총 <span className='font-semibold tabular-nums text-foreground'>{total}</span> 건
      </span>
      {items.map(([label, value]) => (
        <span key={label}>
          {label}: <span className='tabular-nums'>{value ?? '-'}</span> 건
        </span>
      ))}
      <span className='ml-auto'>조회 사업장: {scope}</span>
      {viewTotal > loaded && <span className='text-amber-600'>{loaded}건까지 표시 - 조회조건을 좁히세요</span>}
    </div>
  );
}
